import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Document as PdfDocument,
  Image as PdfImage,
  Page as PdfPage,
  StyleSheet,
  Text as PdfText,
  View as PdfView,
  pdf,
} from "@react-pdf/renderer";

import fallbackLogo from "../../../assets/pawsi.jpeg";

const money = (value) => `Q${Number(value || 0).toFixed(2)}`;

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const reportDate = () => new Date().toLocaleString("es-GT");

const fileDate = () => new Date().toISOString().slice(0, 10);

const sanitizeFilePart = (value) =>
  String(value || "Sucursal")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80) || "Sucursal";

const buildReportMeta = (count, valuationMode) => {
  const isGeneral = String(count?.count_type || "").toUpperCase() === "GENERAL";
  return {
    branchName:
      count?.branch_name ||
      count?.branch_alias ||
      count?.branch?.alias ||
      count?.branch?.name ||
      count?.branch?.business_name ||
      "Sucursal",
    confirmedBy: count?.confirmed_by_name || count?.reviewed_by_name || count?.applied_by_name || "-",
    countId: count?.id || "-",
    countLabel: isGeneral ? "Inventario general" : "Conteo selectivo",
    fileLabel: isGeneral ? "Inventario_General" : "Selectivo",
    moduleLabel: isGeneral ? "inventario general" : "conteo selectivo",
    valuationLabel: valuationMode === "SALE" ? "Precio venta" : "Precio costo",
    generatedAt: reportDate(),
    comment: count?.comment || "",
  };
};

const buildReportHtml = ({ count, rows, valuationMode, total }) => {
  const meta = buildReportMeta(count, valuationMode);
  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Reporte valorizado ${escapeHtml(meta.moduleLabel)} ${escapeHtml(meta.countId)}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #1f2933; margin: 28px; }
    header { display: flex; justify-content: space-between; gap: 16px; border-bottom: 3px solid #6f7b31; padding-bottom: 12px; }
    img { width: 58px; height: 58px; object-fit: contain; }
    h1 { margin: 0; font-size: 20px; color: #5f6729; text-transform: uppercase; }
    .muted { color: #6b7280; font-size: 12px; }
    .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px 18px; margin: 18px 0; font-size: 12px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th { text-align: left; background: #2c2c2a; color: white; padding: 8px; }
    td { border-bottom: 1px solid #e5e7eb; padding: 8px; vertical-align: top; }
    .num { text-align: right; white-space: nowrap; }
    .total { margin-top: 16px; text-align: right; font-size: 16px; font-weight: 700; }
    footer { margin-top: 28px; color: #6b7280; font-size: 11px; border-top: 1px solid #e5e7eb; padding-top: 8px; }
    @media print { body { margin: 18px; } }
  </style>
</head>
<body>
  <header>
    <div style="display:flex; gap:12px; align-items:center;">
      <img src="${fallbackLogo}" />
      <div>
        <h1>Reporte valorizado de faltantes</h1>
        <div class="muted">${escapeHtml(meta.branchName)}</div>
      </div>
    </div>
    <div class="muted" style="text-align:right;">${escapeHtml(meta.generatedAt)}<br/>${escapeHtml(meta.countLabel)} #${escapeHtml(meta.countId)}</div>
  </header>
  <section class="meta">
    <div><strong>Sucursal:</strong> ${escapeHtml(meta.branchName)}</div>
    <div><strong>Fecha:</strong> ${escapeHtml(meta.generatedAt)}</div>
    <div><strong>${escapeHtml(meta.countLabel)}:</strong> #${escapeHtml(meta.countId)}</div>
    <div><strong>Confirmado por:</strong> ${escapeHtml(meta.confirmedBy)}</div>
    <div><strong>Criterio de valorizacion:</strong> ${escapeHtml(meta.valuationLabel)}</div>
  </section>
  <table>
    <thead><tr><th>#</th><th>Producto</th><th>Codigo</th><th>Cantidad faltante real</th><th class="num">Precio usado</th><th class="num">Total</th></tr></thead>
    <tbody>
      ${rows.map((row, index) => `<tr><td>${escapeHtml(row.number || index + 1)}</td><td><strong>${escapeHtml(row.product)}</strong>${row.packaging ? `<br/><span class="muted">${escapeHtml(row.packaging)}</span>` : ""}</td><td>${escapeHtml(row.code)}</td><td>${escapeHtml(row.qty)}</td><td class="num">${money(row.price)}</td><td class="num">${money(row.total)}</td></tr>`).join("") || `<tr><td colspan="6">Sin faltantes reales despues de cruces.</td></tr>`}
    </tbody>
  </table>
  <div class="total">Total general: ${money(total)}</div>
  <footer>Generado desde el modulo de ${escapeHtml(meta.moduleLabel)}. ${escapeHtml(meta.comment)}</footer>
</body>
</html>`;
};

const pdfStyles = StyleSheet.create({
  page: { padding: 28, fontSize: 9, color: "#1f2933", fontFamily: "Helvetica" },
  header: { flexDirection: "row", justifyContent: "space-between", borderBottomWidth: 2, borderBottomColor: "#6f7b31", paddingBottom: 10 },
  brandRow: { flexDirection: "row", alignItems: "center" },
  logo: { width: 42, height: 42, objectFit: "contain", marginRight: 8 },
  title: { fontSize: 14, color: "#5f6729", fontWeight: 700, textTransform: "uppercase" },
  muted: { color: "#6b7280", fontSize: 8 },
  meta: { flexDirection: "row", flexWrap: "wrap", gap: 6, marginVertical: 12 },
  metaCell: { width: "48%", fontSize: 9 },
  table: { borderWidth: 1, borderColor: "#d1d5db" },
  row: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#e5e7eb" },
  head: { backgroundColor: "#2c2c2a", color: "#ffffff", fontWeight: 700 },
  cellNumber: { width: "6%", padding: 5 },
  cellProduct: { width: "31%", padding: 5 },
  cellCode: { width: "13%", padding: 5 },
  cellQty: { width: "20%", padding: 5 },
  cellMoney: { width: "15%", padding: 5, textAlign: "right" },
  total: { marginTop: 12, textAlign: "right", fontSize: 12, fontWeight: 700 },
  footer: { marginTop: 20, paddingTop: 8, borderTopWidth: 1, borderTopColor: "#e5e7eb", color: "#6b7280", fontSize: 8 },
});

function ValuedShortagePdfDocument({ count, rows, valuationMode, total, logoUrl = fallbackLogo }) {
  const meta = buildReportMeta(count, valuationMode);
  return (
    <PdfDocument>
      <PdfPage size="LETTER" style={pdfStyles.page}>
        <PdfView style={pdfStyles.header}>
          <PdfView style={pdfStyles.brandRow}>
            {logoUrl ? <PdfImage src={logoUrl} style={pdfStyles.logo} /> : null}
            <PdfView>
              <PdfText style={pdfStyles.title}>Reporte valorizado de faltantes</PdfText>
              <PdfText style={pdfStyles.muted}>{meta.branchName}</PdfText>
            </PdfView>
          </PdfView>
          <PdfView>
            <PdfText style={pdfStyles.muted}>{meta.generatedAt}</PdfText>
            <PdfText style={pdfStyles.muted}>{meta.countLabel} #{meta.countId}</PdfText>
          </PdfView>
        </PdfView>

        <PdfView style={pdfStyles.meta}>
          <PdfText style={pdfStyles.metaCell}>Sucursal: {meta.branchName}</PdfText>
          <PdfText style={pdfStyles.metaCell}>Fecha: {meta.generatedAt}</PdfText>
          <PdfText style={pdfStyles.metaCell}>{meta.countLabel}: #{meta.countId}</PdfText>
          <PdfText style={pdfStyles.metaCell}>Confirmado por: {meta.confirmedBy}</PdfText>
          <PdfText style={pdfStyles.metaCell}>Criterio: {meta.valuationLabel}</PdfText>
        </PdfView>

        <PdfView style={pdfStyles.table}>
          <PdfView style={[pdfStyles.row, pdfStyles.head]} fixed>
            <PdfText style={pdfStyles.cellNumber}>#</PdfText>
            <PdfText style={pdfStyles.cellProduct}>Producto</PdfText>
            <PdfText style={pdfStyles.cellCode}>Codigo</PdfText>
            <PdfText style={pdfStyles.cellQty}>Cantidad faltante real</PdfText>
            <PdfText style={pdfStyles.cellMoney}>Precio</PdfText>
            <PdfText style={pdfStyles.cellMoney}>Total</PdfText>
          </PdfView>
          {rows.length > 0 ? (
            rows.map((row, index) => (
              <PdfView key={`${row.code}-${index}`} style={pdfStyles.row} wrap={false}>
                <PdfText style={pdfStyles.cellNumber}>{row.number || index + 1}</PdfText>
                <PdfView style={pdfStyles.cellProduct}>
                  <PdfText>{row.product}</PdfText>
                  {row.packaging ? <PdfText style={pdfStyles.muted}>{row.packaging}</PdfText> : null}
                </PdfView>
                <PdfText style={pdfStyles.cellCode}>{row.code}</PdfText>
                <PdfText style={pdfStyles.cellQty}>{row.qty}</PdfText>
                <PdfText style={pdfStyles.cellMoney}>{money(row.price)}</PdfText>
                <PdfText style={pdfStyles.cellMoney}>{money(row.total)}</PdfText>
              </PdfView>
            ))
          ) : (
            <PdfView style={pdfStyles.row}>
              <PdfText style={{ padding: 5 }}>Sin faltantes reales despues de cruces.</PdfText>
            </PdfView>
          )}
        </PdfView>
        <PdfText style={pdfStyles.total}>Total general: {money(total)}</PdfText>
        <PdfText style={pdfStyles.footer}>Generado desde el modulo de {meta.moduleLabel}. {meta.comment}</PdfText>
      </PdfPage>
    </PdfDocument>
  );
}

function ValuedShortageReportDialog({
  open,
  onClose,
  count,
  rows = [],
  valuationMode,
  onChangeValuationMode,
}) {
  const printFrameRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const total = useMemo(() => rows.reduce((sum, row) => sum + Number(row?.total || 0), 0), [rows]);
  const meta = useMemo(() => buildReportMeta(count, valuationMode), [count, valuationMode]);
  const reportHtml = useMemo(
    () => buildReportHtml({ count, rows, valuationMode, total }),
    [count, rows, total, valuationMode]
  );

  const handlePrint = () => {
    const frameWindow = printFrameRef.current?.contentWindow;
    if (!frameWindow) {
      setError("No se pudo preparar la impresion.");
      return;
    }
    setError("");
    frameWindow.focus();
    frameWindow.print();
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError("");
    try {
      let blob;
      try {
        blob = await pdf(
          <ValuedShortagePdfDocument count={count} rows={rows} valuationMode={valuationMode} total={total} />
        ).toBlob();
      } catch {
        blob = await pdf(
          <ValuedShortagePdfDocument count={count} rows={rows} valuationMode={valuationMode} total={total} logoUrl="" />
        ).toBlob();
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Reporte_Faltantes_${meta.fileLabel}_${meta.countId}_${sanitizeFilePart(meta.branchName)}_${fileDate()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("No se pudo descargar el PDF.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Reporte valorizado</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            select
            size="small"
            label="Valorar por"
            value={valuationMode}
            onChange={(event) => onChangeValuationMode?.(event.target.value)}
            sx={{ maxWidth: 260 }}
          >
            <MenuItem value="COST">Costo</MenuItem>
            <MenuItem value="SALE">Venta</MenuItem>
          </TextField>

          <Paper variant="outlined" sx={{ p: 2, bgcolor: "#ffffff", color: "#1f2933" }}>
            <Stack spacing={1.4}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Box component="img" src={fallbackLogo} alt="" sx={{ width: 54, height: 54, objectFit: "contain" }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#5f6729", textTransform: "uppercase" }}>
                      Reporte valorizado de faltantes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{meta.branchName}</Typography>
                  </Box>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right" }}>
                  {meta.generatedAt}
                  <br />
                  {meta.countLabel} #{meta.countId}
                </Typography>
              </Stack>
              <Divider />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 0.8 }}>
                <Typography variant="body2"><strong>Sucursal:</strong> {meta.branchName}</Typography>
                <Typography variant="body2"><strong>Fecha:</strong> {meta.generatedAt}</Typography>
                <Typography variant="body2"><strong>{meta.countLabel}:</strong> #{meta.countId}</Typography>
                <Typography variant="body2"><strong>Confirmado por:</strong> {meta.confirmedBy}</Typography>
                <Typography variant="body2"><strong>Criterio:</strong> {meta.valuationLabel}</Typography>
              </Box>
              <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#2c2c2a" }}>
                    <TableCell sx={{ color: "#fff", fontWeight: 800 }}>#</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Producto</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Codigo</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Cantidad faltante real</TableCell>
                    <TableCell align="right" sx={{ color: "#fff", fontWeight: 800 }}>Precio</TableCell>
                    <TableCell align="right" sx={{ color: "#fff", fontWeight: 800 }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length > 0 ? rows.map((row, index) => (
                    <TableRow key={`${row.code}-${index}`}>
                      <TableCell>{row.number || index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {row.product}
                        </Typography>
                        {row.packaging ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {row.packaging}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{row.code}</TableCell>
                      <TableCell>{row.qty}</TableCell>
                      <TableCell align="right">{money(row.price)}</TableCell>
                      <TableCell align="right">{money(row.total)}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={6}>Sin faltantes reales despues de cruces.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Typography sx={{ textAlign: "right", fontWeight: 800 }}>
                Total general: {money(total)}
              </Typography>
            </Stack>
          </Paper>
        </Stack>
        <iframe
          ref={printFrameRef}
          title="Reporte valorizado para imprimir"
          srcDoc={reportHtml}
          style={{ position: "absolute", width: 0, height: 0, border: 0, visibility: "hidden" }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button onClick={handlePrint}>Imprimir</Button>
        <Button variant="contained" onClick={handleDownload} disabled={downloading}>
          {downloading ? "Descargando..." : "Descargar PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ValuedShortageReportDialog;
